// src/features/fourlink-config/components/forms/FileUpload.jsx

import React, { useState } from 'react';
import * as XLSX from 'xlsx';

export const FileUpload = ({ onFileProcessed, selectedTipologias }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const processExcelFile = async (file) => {
    setIsProcessing(true);
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'buffer' });
      
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      if (jsonData.length === 0) {
        throw new Error('El archivo Excel está vacío');
      }
      
      const firstRow = jsonData[0];
      const columns = Object.keys(firstRow);
      
      let nombreKey = null;
      let tipologiaKey = null;
      
      for (const key of columns) {
        const keyLower = key.toLowerCase().trim();
        if (keyLower === 'nombre' || keyLower === 'name') {
          nombreKey = key;
        }
        if (keyLower === 'tipologia' || keyLower === 'tipología' || keyLower === 'tipo') {
          tipologiaKey = key;
        }
      }
      
      if (!nombreKey || !tipologiaKey) {
        throw new Error(`Formato incorrecto. El Excel debe tener exactamente estas columnas:
- "Nombre" (para el nombre del centro)  
- "Tipologia" (para el tipo de centro)

Columnas encontradas: ${columns.join(', ')}`);
      }
      
      const processedData = [];
      
      jsonData.forEach((row) => {
        const nombre = row[nombreKey]?.toString()?.trim();
        const tipologia = row[tipologiaKey]?.toString()?.trim();
        
        if (nombre && tipologia) {
          processedData.push({ nombre, tipologia });
        }
      });
      
      if (processedData.length === 0) {
        throw new Error('No se encontraron datos válidos. Verifica que las celdas contengan texto.');
      }
      
      // Llamar al callback con los datos procesados
      onFileProcessed?.(processedData);
      
      setIsProcessing(false);
      setUploadedFile(null);
      
    } catch (error) {
      setIsProcessing(false);
      setUploadedFile(null);
      console.error('Error:', error);
      alert(`Error al procesar el archivo:\n\n${error.message}`);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          file.type === 'application/vnd.ms-excel' ||
          file.name.endsWith('.xlsx') || 
          file.name.endsWith('.xls')) {
        setUploadedFile(file);
        processExcelFile(file);
      } else {
        alert('Por favor, arrastra un archivo Excel (.xlsx o .xls)');
      }
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadedFile(file);
      processExcelFile(file);
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
      <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
        Importación Masiva desde Excel
      </h4>
      <p className="text-sm text-blue-700 mb-4">
        Arrastra tu archivo Excel aquí o haz clic para seleccionarlo. 
        El archivo debe tener columnas "Nombre" y "Tipología".
      </p>
      
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
          dragActive 
            ? 'border-blue-400 bg-blue-100' 
            : 'border-blue-300 hover:border-blue-400 hover:bg-blue-50'
        } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !isProcessing && document.getElementById('excel-upload').click()}
      >
        {isProcessing ? (
          <div className="flex items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-blue-700 font-medium">Procesando archivo Excel...</span>
          </div>
        ) : (
          <>
            <div className="text-4xl mb-3 text-blue-600" aria-hidden="true">
              <svg className="w-10 h-10 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 5 17 10" />
                <line x1="12" y1="5" x2="12" y2="16" />
              </svg>
            </div>
            <p className="text-blue-700 font-medium mb-2">
              {dragActive ? 'Suelta el archivo aquí' : 'Arrastra tu archivo Excel aquí'}
            </p>
            <p className="text-sm text-blue-600">
              o haz clic para seleccionar archivo (.xlsx, .xls)
            </p>
            {uploadedFile && (
              <p className="text-sm text-green-600 mt-2">
                Archivo: {uploadedFile.name}
              </p>
            )}
          </>
        )}
      </div>
      
      <input
        id="excel-upload"
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileInput}
        className="hidden"
        disabled={isProcessing}
      />
      
      <div className="mt-4 p-3 bg-white border border-blue-200 rounded">
        <p className="text-sm font-medium text-blue-800 mb-2">Formato requerido del Excel:</p>
        <div className="text-xs text-blue-700 font-mono bg-blue-50 p-2 rounded">
          <div className="grid grid-cols-2 gap-4 border-b pb-1 mb-1 font-bold">
            <span>Nombre</span>
            <span>Tipologia</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <span>Centro Madrid Norte</span>
            <span>Restaurante Con terraza</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <span>Cafetería Plaza Sol</span>
            <span>Cafetería</span>
          </div>
        </div>
        <p className="text-xs text-red-600 mt-2">
          IMPORTANTE: Las columnas deben llamarse exactamente "Nombre" y "Tipologia" (sin tilde)
        </p>
      </div>
    </div>
  );
};